import { Component, Input, Output, EventEmitter, OnDestroy, ViewChild, ElementRef, HostListener } from '@angular/core';
import { interval, Subscription, takeUntil, fromEvent, tap, catchError, of } from 'rxjs';

@Component({
  selector: 'app-input-number-customized',
  templateUrl: './input-number-customized.component.html',
  styleUrls: ['./input-number-customized.component.scss'],
})
export class InputNumberCustomizedComponent implements OnDestroy {
  @Output() valueChange = new EventEmitter<number>();

  @Input() label: string = 'Label';
  @Input() value: number = 0;
  @Input() min: number = 0;
  @Input() max: number;
  @Input() step: number = 1;
  @Input() tooltip: string = 'Information about this field.';
  @Input() disabled: boolean = false;
  @Input() unit: string = '';
  @Input() restrictByStep: boolean = false;
  @Input() largeQuantity: boolean = false;
  /** Control for large quantities in the step, p.e if step is a larger decimal */
  @Input() pressIntervalMs: number = 50;

  @ViewChild('inputNumber') inputNumberRef: ElementRef<HTMLInputElement>;

  @ViewChild('upButton') upButton!: ElementRef<HTMLButtonElement>;
  @ViewChild('downButton') downButton!: ElementRef<HTMLButtonElement>;

  private incrementSubscription: Subscription | null = null;
  private decrementSubscription: Subscription | null = null;

  private disabledPressIntervalMs: number = 5000;

  constructor() {
  }

  @HostListener('mouseleave', ['$event'])
  onMouseLeave(event: MouseEvent): void {
    this.stopAnyActiveIntervals();
  }

  @HostListener('mouseenter', ['$event'])
  onMouseEnter(event: MouseEvent): void {
    this.stopAnyActiveIntervals();
  }

  capValue() {
    if (this.max !== undefined && parseFloat(this.inputNumberRef.nativeElement.value || '0') > this.max) {
      this.inputNumberRef.nativeElement.value = this.max.toString();
      this.value = this.max;
      return true;
    }

    if (this.min !== undefined && parseFloat(this.inputNumberRef.nativeElement.value || '0') < this.min) {
      this.inputNumberRef.nativeElement.value = this.min.toString();
      this.value = this.min;
      return true;
    }

    return false;
  }

  private restrictValueByStep() {
    if (!this.restrictByStep) {
      return;
    }
    const parsedValue = parseFloat(this.inputNumberRef.nativeElement.value || '0');
    if (isNaN(parsedValue)) {
      return;
    }
    const remainder = (parsedValue - this.min) % this.step;
    this.value = this.min + Math.ceil((parsedValue - this.min + remainder) / this.step) * this.step;
    this.inputNumberRef.nativeElement.value = this.value.toString();
  }

  onValueChange(newValue: number): void {
    if (this.disabled) {
      return;
    }
    if (this.capValue()) {
      return;
    }
    this.restrictValueByStep();

    const parsedValue = parseFloat(newValue as any);
    if (!isNaN(parsedValue)) {
      if (this.min === undefined && this.max === undefined) {
        this.value = parsedValue;
      } else if (this.min !== undefined && this.max !== undefined) {
        this.value = Math.min(Math.max(parsedValue, this.min), this.max);
      }
    } else {
      this.value = this.min;
    }

    this.emitValueChange();
    this.stopAnyActiveIntervals();
  }

  private getPressIntervalMs() {
    // 400ms for enabled and 5000ms for disabled
    // the 400ms cannot be too low, because it could bug the input increment/decrement handling
    // the 5000ms is to avoid opening doors to bug behavior if user click and hold the button in the disabled state
    return !this.disabled ? this.pressIntervalMs : this.disabledPressIntervalMs;
  }

  // This creates a "press and hold" functionality
  startIncrementing(): void {
    this.stopAnyActiveIntervals();

    this.incrementSubscription = interval(this.getPressIntervalMs())
      .pipe(
        takeUntil(fromEvent(this.upButton.nativeElement, 'mouseup')),
        takeUntil(fromEvent(this.upButton.nativeElement, 'mouseleave')),
        tap(() => this.incrementValue()),
        catchError((error) => {
          this.stopAnyActiveIntervals();
          return of(null);
        })
      )
      .subscribe();
  }

  stopIncrementing(): void {
    if (this.incrementSubscription) {
      this.incrementSubscription.unsubscribe();
      this.incrementSubscription = null;
    }
    this.emitValueChange();
  }

  incrementValue(): void {
    if (this.disabled) {
      this.stopAnyActiveIntervals();
      return;
    }
    if (this.capValue()) {
      return;
    }
    this.value = Math.min(Math.max((this.value || 0) + this.step, this.min), this.max);
  }

  // This creates a "press and hold" functionality
  startDecrementing(): void {
    this.stopAnyActiveIntervals();

    this.decrementSubscription = interval(this.getPressIntervalMs())
      .pipe(
        takeUntil(fromEvent(this.downButton.nativeElement, 'mouseup')),
        takeUntil(fromEvent(this.downButton.nativeElement, 'mouseleave')),
        tap(() => this.decrementValue())
      )
      .subscribe();
  }

  stopDecrementing(): void {
    if (this.decrementSubscription) {
      this.decrementSubscription.unsubscribe();
      this.decrementSubscription = null;
    }
    this.emitValueChange();
  }

  decrementValue(): void {
    if (this.disabled) {
      this.stopAnyActiveIntervals();
      return;
    }
    if (this.capValue()) {
      return;
    }
    this.value = Math.min(Math.max((this.value || 0) - this.step, this.min), this.max);
  }

  private stopAnyActiveIntervals(): void {
    this.stopIncrementing();
    this.stopDecrementing();
  }

  private emitValueChange(): void {
    this.valueChange.emit(this.value);
  }

  ngOnDestroy(): void {
    this.stopAnyActiveIntervals();
  }
}
